import { getErrorMessage } from '@billda/shared-ui';
import { BaseState, AsyncOperationOptions } from './storeTypes';

export type { BaseState } from './storeTypes';

// Strongly-typed store setter
export type StoreSetter<T> = (
  partial: Partial<T> | ((state: T) => Partial<T>)
) => void;

// Authentication helper factory with proper typing
export const createAuthHelper = <T extends { error: string | null }>(
  getAuthToken: () => string | null
) => ({
  requireAuth: (set: StoreSetter<T>): string | null => {
    const token = getAuthToken();
    if (!token) {
      set({ error: 'Authentication required' } as Partial<T>);
      return null;
    }
    return token;
  },
});

// Error handling helper factory with proper typing
const createErrorHandler = <T extends BaseState>() => ({
  handleAsyncError: (
    error: unknown,
    operation: string,
    set: StoreSetter<T>
  ) => {
    console.error(`Error during ${operation}:`, error);
    set({
      error: getErrorMessage(error),
      loading: false,
    } as Partial<T>);
  },
});

// Loading state wrapper factory with proper typing
export const createLoadingWrapper = <T extends BaseState>() => ({
  withLoading: async (
    set: StoreSetter<T>,
    operation: () => Promise<void>,
    options: AsyncOperationOptions = {}
  ) => {
    const { skipLoading = false, customErrorHandler } = options;

    if (!skipLoading) {
      set({ loading: true, error: null } as Partial<T>);
    }

    try {
      await operation();
      if (!skipLoading) {
        set({ loading: false } as Partial<T>);
      }
    } catch (error) {
      if (customErrorHandler) {
        customErrorHandler(error);
      } else {
        const { handleAsyncError } = createErrorHandler<T>();
        handleAsyncError(error, 'operation', set);
      }
    }
  },

  withLoadingReturn: async <TReturn>(
    set: StoreSetter<T>,
    operation: () => Promise<TReturn>,
    options: AsyncOperationOptions = {}
  ): Promise<TReturn | null> => {
    const { skipLoading = false, customErrorHandler } = options;

    if (!skipLoading) {
      set({ loading: true, error: null } as Partial<T>);
    }

    try {
      const result = await operation();
      if (!skipLoading) {
        set({ loading: false } as Partial<T>);
      }
      return result;
    } catch (error) {
      if (customErrorHandler) {
        customErrorHandler(error);
      } else {
        const { handleAsyncError } = createErrorHandler<T>();
        handleAsyncError(error, 'operation', set);
      }
      return null;
    }
  },
});

// Common state update patterns
export const createStateUpdaters = () => ({
  // Update a single item in an array by id
  updateItemInArray: <TItem extends { id: string | number }>(
    array: TItem[],
    id: string | number,
    updates: Partial<TItem>
  ): TItem[] => {
    return array.map(item => (item.id === id ? { ...item, ...updates } : item));
  },

  // Remove item from array by id
  removeItemFromArray: <TItem extends { id: string | number }>(
    array: TItem[],
    id: string | number
  ): TItem[] => {
    return array.filter(item => item.id !== id);
  },

  // Add item to array (with optional deduplication)
  addItemToArray: <TItem extends { id?: string | number }>(
    array: TItem[],
    item: TItem,
    deduplicate: boolean = false
  ): TItem[] => {
    if (
      deduplicate &&
      item.id &&
      array.some(existing => existing.id === item.id)
    ) {
      return array;
    }
    return [...array, item];
  },
});
